<template>
    <section
      ref="sectionRef"
      class="general-banner"
      :class="{
        'general-banner--visible': isVisible
      }"
    >
      <div class="general-banner__inner">
        <!-- Header -->
        <div class="general-banner__header">
          <span class="general-banner__eyebrow">
            HR3 для работодателей
          </span>
  
          <h1 class="general-banner__title">
            Какие задачи мы решаем
            <span>для вас?</span>
          </h1>
  
          <p class="general-banner__description">
            Мы собрали инструменты, которые помогают
            быстрее находить сильных кандидатов,
            экономить время команды и принимать решения
            на основе данных, а не бесконечного просмотра
            откликов.
          </p>
        </div>
  
        <!-- Illustration -->
          <!-- Illustration -->
          <div class="general-banner__visual">
            <div class="ambient ambient--left"></div>
            <div class="ambient ambient--right"></div>

            <img
              :src="currentImage"
              class="general-banner__image"
              alt="Преимущества HR3 для работодателей"
              draggable="false"
            />

            <!-- AI description -->
            <div class="ai-analysis">
              <span class="ai-analysis__label">
                AI HR3
              </span>

              <h2 class="ai-analysis__title">
                AI-анализ каждого отклика
              </h2>

              <div class="ai-analysis__content">
                <p>
                  Наш AI изучает не только резюме кандидата. Он
                  одновременно анализирует вакансию, профиль вашей
                  компании и данные откликнувшегося соискателя, чтобы
                  оценить кандидата именно в контексте конкретной позиции.
                </p>

                <p>
                  Система выделяет релевантный опыт и ключевые навыки,
                  сопоставляет их с требованиями вакансии и формирует
                  несколько уровней оценки:
                  <strong>
                    общий мэтч, профессиональный мэтч и соответствие
                    ключевым требованиям.
                  </strong>
                </p>

                <p>
                  Вместо необходимости самостоятельно изучать каждый
                  профиль вы получаете структурированную картину
                  кандидата: его сильные стороны, релевантный опыт,
                  ключевые навыки и понятное AI-заключение.
                </p>

                <p class="ai-analysis__result">
                  Так проще быстрее находить сильных кандидатов и
                  понимать, с кем действительно стоит продолжить общение.
                </p>
              </div>
            </div>
          </div>
      </div>
    </section>
  
    <div class="separator"></div>
  </template>
  
  <script setup>
  import {
    computed,
    onMounted,
    onUnmounted,
    ref
  } from "vue";
  
  const lightImage = new URL(
    "../../assets/GeneralBanner-light.png",
    import.meta.url
  ).href;
  
  const darkImage = new URL(
    "../../assets/GeneralBanner-dark.png",
    import.meta.url
  ).href;
  
  const sectionRef = ref(null);
  
  const isVisible = ref(false);
  
  const theme = ref(
    document.documentElement.getAttribute(
      "data-theme"
    ) || "light"
  );
  
  const currentImage = computed(() => {
    return theme.value === "dark"
      ? darkImage
      : lightImage;
  });
  
  let themeObserver = null;
  let intersectionObserver = null;
  
  onMounted(() => {
    themeObserver = new MutationObserver(() => {
      theme.value =
        document.documentElement.getAttribute(
          "data-theme"
        ) || "light";
    });
  
    themeObserver.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "data-theme"
        ]
      }
    );
  
    intersectionObserver =
      new IntersectionObserver(
        entries => {
          const entry = entries[0];
  
          if (!entry?.isIntersecting) {
            return;
          }
  
          isVisible.value = true;
  
          intersectionObserver?.disconnect();
        },
        {
          threshold: 0.12
        }
      );
  
    if (sectionRef.value) {
      intersectionObserver.observe(
        sectionRef.value
      );
    } else {
      requestAnimationFrame(() => {
        isVisible.value = true;
      });
    }
  });
  
  onUnmounted(() => {
    themeObserver?.disconnect();
    intersectionObserver?.disconnect();
  });
  
  </script>
  
  <style scoped>

  * {
    box-sizing: border-box;
  }
  
  .general-banner {
    position: relative;
  
    width: 100%;
  
    overflow: hidden;
  
    background: var(--background);
  
    opacity: 0;
    transform: translateY(16px);
  
    transition:
      opacity 0.75s ease,
      transform 0.75s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        );
  }
  
  .general-banner--visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  .general-banner__inner {
    width: 100%;
    max-width: 1440px;
  
    margin: 0 auto;
  
    padding:
      84px
      64px
      50px;
  }
  
  /* Header */
  
  .general-banner__header {
    width: 100%;
    max-width: 940px;
  
    margin-bottom: 32px;
  }
  
  .general-banner__eyebrow {
    display: inline-flex;
    align-items: center;
  
    margin-bottom: 14px;
  
    color: #2277e6;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 12px;
    line-height: 1;
    letter-spacing: 0.07em;
  
    text-transform: uppercase;
  }
  
  .general-banner__title {
    margin:
      0
      0
      18px;
  
    color: var(--text);
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size:
      clamp(
        42px,
        4.5vw,
        68px
      );
  
    line-height: 0.98;
    letter-spacing: -0.045em;
  }
  
  .general-banner__title span {
    color: #2277e6;
  }
  
  .general-banner__description {
    max-width: 760px;
  
    margin: 0;
  
    color:
      var(
        --text-secondary,
        #6f7682
      );
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 17px;
    line-height: 1.55;
    letter-spacing: -0.015em;
  }
  
  /* Visual */
  
  .general-banner__visual {
    position: relative;
  
    width: 100%;
    min-height: 560px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    isolation: isolate;
  }
  
  .general-banner__image {
    position: relative;
    z-index: 2;
  
    width: 100%;
    max-width: 1320px;
    height: auto;
  
    display: block;
  
    object-fit: contain;
  
    user-select: none;
  
    filter:
      drop-shadow(
        0
        24px
        46px
        rgba(
          32,
          67,
          120,
          0.06
        )
      );
  
    transform:
      translateY(12px)
      scale(0.985);
  
    opacity: 0;
  
    transition:
      opacity 0.9s ease 0.1s,
      transform 0.9s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        )
        0.1s;
  }
  
  .general-banner--visible
  .general-banner__image {
    opacity: 1;
  
    transform:
      translateY(0)
      scale(1);
  }
  
  /* Ambient */
  
  .ambient {
    position: absolute;
    z-index: 0;
  
    border-radius: 50%;
  
    pointer-events: none;
  
    filter: blur(110px);
  
    opacity: 0.12;
  
    animation:
      ambientFloat
      14s
      ease-in-out
      infinite;
  }
  
  .ambient--left {
    width: 360px;
    height: 360px;
  
    left: 5%;
    top: 18%;
  
    background: #4a8df6;
  }
  
  .ambient--right {
    width: 300px;
    height: 300px;
  
    right: 9%;
    bottom: 12%;
  
    background: #7865ef;
  
    animation-delay: -6s;
  }
  
  @keyframes ambientFloat {
    0%,
    100% {
      transform:
        translate3d(
          0,
          0,
          0
        )
        scale(1);
    }
  
    50% {
      transform:
        translate3d(
          20px,
          -14px,
          0
        )
        scale(1.05);
    }
  }
  
  /* Separator */
  
  .separator {
    width: 100%;
    height: 1px;
  
    background:
      var(
        --separator_home,
        rgba(
          0,
          0,
          0,
          0.08
        )
      );
  }
  
  /* Dark */
  
  html[data-theme="dark"]
  .general-banner__description {
    color: #9ba2ae;
  }
  
  html[data-theme="dark"]
  .general-banner__image {
    filter:
      drop-shadow(
        0
        24px
        55px
        rgba(
          0,
          0,
          0,
          0.22
        )
      );
  }
  
  html[data-theme="dark"]
  .ambient {
    opacity: 0.17;
  }
  
  /* Laptop */
  
  @media (
    max-width: 1366px
  ) {
    .general-banner__inner {
      padding:
        64px
        48px
        38px;
    }
  
    .general-banner__header {
      max-width: 820px;
  
      margin-bottom: 20px;
    }
  
    .general-banner__title {
      font-size:
        clamp(
          40px,
          4vw,
          56px
        );
    }
  
    .general-banner__description {
      max-width: 700px;
  
      font-size: 15px;
    }
  
    .general-banner__visual {
      min-height: 480px;
    }
  
    .general-banner__image {
      max-width: 1160px;
    }
  }
  
  /* 13" laptops */
  
  @media (
    min-width: 769px
  ) and (
    max-height: 820px
  ) {
    .general-banner__inner {
      padding:
        42px
        46px
        24px;
    }
  
    .general-banner__header {
      margin-bottom: 12px;
    }
  
    .general-banner__eyebrow {
      margin-bottom: 9px;
  
      font-size: 10px;
    }
  
    .general-banner__title {
      margin-bottom: 12px;
  
      font-size:
        clamp(
          38px,
          3.8vw,
          50px
        );
    }
  
    .general-banner__description {
      max-width: 680px;
  
      font-size: 13px;
      line-height: 1.45;
    }
  
    .general-banner__visual {
      min-height: 400px;
    }
  
    .general-banner__image {
      max-width: 1050px;
    }
  }
  
  /* Tablet */
  
  @media (
    max-width: 900px
  ) {
    .general-banner__inner {
      padding:
        52px
        28px
        32px;
    }
  
    .general-banner__title {
      font-size:
        clamp(
          38px,
          7vw,
          52px
        );
  
      line-height: 1;
    }
  
    .general-banner__description {
      font-size: 14px;
    }
  
    .general-banner__visual {
      min-height: 400px;
    }
  
    .general-banner__image {
      width: 112%;
      max-width: none;
    }
  }
  
  /* Mobile */
  
  @media (
    max-width: 768px
  ) {
    .general-banner__inner {
      padding:
        40px
        18px
        24px;
    }
  
    .general-banner__header {
      margin-bottom: 22px;
    }
  
    .general-banner__eyebrow {
      margin-bottom: 10px;
  
      font-size: 9px;
    }
  
    .general-banner__title {
      margin-bottom: 14px;
  
      font-size: 36px;
      line-height: 1.03;
    }
  
    .general-banner__description {
      font-size: 13px;
      line-height: 1.5;
    }
  
    .general-banner__visual {
      min-height: 0;
  
      margin:
        0
        -12px;
    }
  
    .general-banner__image {
      width: 125%;
  
      max-width: none;
  
      margin-left: 4%;
  
      filter: none;
    }
  
    .ambient {
      display: none;
    }
  }
  
  @media (
    max-width: 480px
  ) {
    .general-banner__inner {
      padding:
        34px
        16px
        20px;
    }
  
    .general-banner__title {
      font-size: 31px;
    }
  
    .general-banner__description {
      font-size: 12px;
    }
  
    .general-banner__image {
      width: 138%;
  
      margin-left: 7%;
    }
  }
  
  /* Reduced motion */
  
  @media (
    prefers-reduced-motion:
    reduce
  ) {
    .general-banner,
    .general-banner__image,
    .ambient {
      animation: none;
  
      transition: none;
    }
  
    .general-banner {
      opacity: 1;
  
      transform: none;
    }
  
    .general-banner__image {
      opacity: 1;
  
      transform: none;
    }
  }

  /* AI analysis */

.ai-analysis {
  position: absolute;
  z-index: 5;

  top: 320px;
  right: 1.5%;

  width: 32%;
  max-width: 430px;

  padding:
    22px
    24px;

  transform:
    translateY(-44%);

  color: #25282e;

  background:
    rgba(
      255,
      255,
      255,
      0.72
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.72
    );

  border-radius: 20px;

  box-shadow:
    0 18px 50px
    rgba(
      30,
      50,
      80,
      0.07
    );

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);

  opacity: 0;

  transition:
    opacity 0.8s ease 0.32s,
    transform 0.8s
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      0.32s;
}

.general-banner--visible
.ai-analysis {
  opacity: 1;

  transform:
    translateY(-50%);
}

.ai-analysis__label {
  display: inline-flex;
  align-items: center;

  margin-bottom: 11px;
  padding:
    5px
    8px;

  color: #2277e6;

  background:
    rgba(
      34,
      119,
      230,
      0.08
    );

  border:
    1px solid
    rgba(
      34,
      119,
      230,
      0.1
    );

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
  line-height: 1;

  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.ai-analysis__title {
  margin:
    0
    0
    13px;

  color: #20242c;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 20px;
  line-height: 1.15;
  letter-spacing: -0.025em;
}

.ai-analysis__content {
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.ai-analysis__content p {
  margin: 0;

  color: #626a77;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11px;
  line-height: 1.48;
}

.ai-analysis__content strong {
  color: #353b45;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-weight: normal;
}

.ai-analysis__result {
  position: relative;

  padding:
    11px
    12px !important;

  color:
    #395a88 !important;

  background:
    linear-gradient(
      135deg,
      rgba(
        34,
        119,
        230,
        0.07
      ),
      rgba(
        83,
        104,
        232,
        0.045
      )
    );

  border:
    1px solid
    rgba(
      34,
      119,
      230,
      0.08
    );

  border-radius: 11px;
}

/* Dark */

html[data-theme="dark"]
.ai-analysis {
  color: #ffffff;

  background:
    rgba(
      28,
      29,
      33,
      0.76
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0 20px 55px
    rgba(
      0,
      0,
      0,
      0.22
    );
}

html[data-theme="dark"]
.ai-analysis__label {
  color: #72a8ff;

  background:
    rgba(
      63,
      137,
      241,
      0.12
    );

  border-color:
    rgba(
      74,
      145,
      246,
      0.16
    );
}

html[data-theme="dark"]
.ai-analysis__title {
  color: #f2f3f5;
}

html[data-theme="dark"]
.ai-analysis__content p {
  color: #a7adb7;
}

html[data-theme="dark"]
.ai-analysis__content strong {
  color: #e1e4e9;
}

html[data-theme="dark"]
.ai-analysis__result {
  color:
    #9fc3ff !important;

  background:
    linear-gradient(
      135deg,
      rgba(
        40,
        135,
        247,
        0.12
      ),
      rgba(
        83,
        104,
        232,
        0.07
      )
    );

  border-color:
    rgba(
      74,
      145,
      246,
      0.14
    );
}

@media (
  min-width: 769px
) and (
  max-height: 820px
) {
  .ai-analysis {
    width: 32%;
    max-width: 330px;

    padding:
      14px
      16px;
  }

  .ai-analysis__title {
    margin-bottom: 8px;

    font-size: 15px;
  }

  .ai-analysis__content {
    gap: 6px;
  }

  .ai-analysis__content p {
    font-size: 8.5px;
    line-height: 1.38;
  }

  .ai-analysis__result {
    padding:
      7px
      8px !important;
  }
}

@media (
  max-width: 900px
) {
  .general-banner__visual {
    flex-direction: column;
  }

  .ai-analysis {
    position: relative;

    top: auto;
    right: auto;

    width: 100%;
    max-width: 680px;

    margin-top: 16px;
    padding:
      18px
      20px;

    transform:
      translateY(10px);
  }

  .general-banner--visible
  .ai-analysis {
    transform:
      translateY(0);
  }

  .ai-analysis__title {
    font-size: 18px;
  }

  .ai-analysis__content p {
    font-size: 11px;
  }
}

  </style>

  